import { FlareSchema } from '@common/flare/flare-schema';
import { insertBr } from '@common/prosemirror/commands/br.command';
import { ApplyConditionsCommand, applyConditions } from '@common/prosemirror/commands/conditions.command';
import { deleteSelection } from '@common/prosemirror/commands/delete-selection.command';
import { insertDropDown, moveFromDropDown, setDropDownHotspot } from '@common/prosemirror/commands/dropdown.command';
import { CreateImagePlaceholdersCommand, insertImagePlaceholders } from '@common/prosemirror/commands/image.command';
import { InsertBlockNodeCommand, insertBlockNode } from '@common/prosemirror/commands/insert-block-node.command';
import { InsertInlineNodeCommand, insertInlineNode } from '@common/prosemirror/commands/insert-inline-node.command';
import { LiftFromInlineNodeOptions, liftFromInlineNode } from '@common/prosemirror/commands/lift-from-inline-node.command';
import { LinkNodeCommand, insertOrEditLink } from '@common/prosemirror/commands/link.command';
import { liftDefinitionListItem, setDefinitionListItemType, sinkDefinitionListItem, splitDefinitionListItem, splitListItem } from '@common/prosemirror/commands/list';
import { appendMultipleChoiceItem } from '@common/prosemirror/commands/multiple-choice.command';
import { SelectNodeCommand, selectNode } from '@common/prosemirror/commands/select-node.command';
import { SetSelectionFormatCommand, setSelectionFormat } from '@common/prosemirror/commands/selection-format.command';
import { setBlockType } from '@common/prosemirror/commands/set-block-type.command';
import { insertOrEditEquation } from '@common/prosemirror/commands/equation.command';
import { CreateSnippetNodeCommand, insertSnippet } from '@common/prosemirror/commands/snippet.command';
import { toggleInlineNode } from '@common/prosemirror/commands/toggle-inline-node.command';
import { WrapInlineNodeCommand, wrapInInlineNode } from '@common/prosemirror/commands/wrap-in-inline-node.command';
import { wrapInList } from '@common/prosemirror/commands/wrap-in-list.command';
import { FlareComputedStylesheetSetShowStylesCommand, setShowStyles, setShowStylesPanel, toggleShowStyles, toggleShowStylesPanel } from '@common/prosemirror/plugins/flare-computed-stylesheet.plugin';
import { TrackedChangesSetTrackChangesCommand, setTrackChanges, toggleTrackChanges } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { CachedAccessor } from '@common/util/cached-accessor.decorator';
import { chainCommands } from 'prosemirror-commands';
import { liftListItem, sinkListItem } from 'prosemirror-schema-list';
import { Command } from 'prosemirror-state';

export class FlareCommands {
  /**
   * The options for deleting annotations with the liftFromInlineNode command.
   * Provided here so they can be used in the portals.
   */
  static readonly AnnotationLiftFromInlineNodeOptions: LiftFromInlineNodeOptions = {
    liftAncestorForEmptySelection: true,
    skipTrackedChanges: true
  };

  constructor(private schema: FlareSchema) { }

  /*
   * Selection
   */
  @CachedAccessor()
  get selectNode(): SelectNodeCommand {
    return selectNode();
  }

  /*
   * Deletion
   */
  @CachedAccessor()
  get deleteSelection(): Command {
    return deleteSelection;
  }

  /*
   * Annotations
   */
  @CachedAccessor()
  get deleteAnnotation(): Command {
    return liftFromInlineNode(this.schema.nodes.madcapannotation, FlareCommands.AnnotationLiftFromInlineNodeOptions);
  }

  @CachedAccessor()
  get insertAnnotation(): WrapInlineNodeCommand {
    return wrapInInlineNode(this.schema.nodes.madcapannotation, {
      limitRangeToOneNode: true,
      skipTrackedChanges: true,
      wrapWordForEmptySelection: true
    });
  }

  /*
   * Break
   */
  @CachedAccessor()
  get insertBr(): Command {
    return insertBr(this.schema.nodes.hard_break);
  }

  /*
   * Lists
   */
  @CachedAccessor()
  get wrapInBulletList(): Command {
    return wrapInList(this.schema.nodes.bullet_list, this.schema.nodes.paragraph, 'keep');
  }

  @CachedAccessor()
  get wrapInOrderedList(): Command {
    return wrapInList(this.schema.nodes.ordered_list, this.schema.nodes.paragraph, 'keep');
  }

  @CachedAccessor()
  get wrapInDefinitionList(): Command {
    return wrapInList(this.schema.nodes.definition_list, this.schema.nodes.mcCentralContainer, 'move-to-item');
  }

  @CachedAccessor()
  get changeDefinitionDescriptionToDefinitionTerm(): Command {
    return setDefinitionListItemType(this.schema.nodes.definition_term, this.schema.nodes.definition_description);
  }

  @CachedAccessor()
  get changeDefinitionTermToDefinitionDescription(): Command {
    return setDefinitionListItemType(this.schema.nodes.definition_description, this.schema.nodes.definition_term);
  }

  @CachedAccessor()
  get indentListItem(): Command {
    return sinkListItem(this.schema.nodes.list_item);
  }

  @CachedAccessor()
  get outdentListItem(): Command {
    return liftListItem(this.schema.nodes.list_item);
  }

  @CachedAccessor()
  get indentDefinitionListItem(): Command {
    return sinkDefinitionListItem(this.schema.nodes.definition_term, this.schema.nodes.definition_description);
  }

  @CachedAccessor()
  get outdentDefinitionListItem(): Command {
    return liftDefinitionListItem(this.schema.nodes.definition_term, this.schema.nodes.definition_description);
  }

  @CachedAccessor()
  get indentAnyListItem(): Command {
    return chainCommands(this.indentListItem, this.indentDefinitionListItem);
  }

  @CachedAccessor()
  get outdentAnyListItem(): Command {
    return chainCommands(this.outdentListItem, this.outdentDefinitionListItem);
  }

  @CachedAccessor()
  get splitListItem(): Command {
    return splitListItem(this.schema.nodes.list_item);
  }

  @CachedAccessor()
  get splitDefinitionListItem(): Command {
    return splitDefinitionListItem(this.schema.nodes.definition_term, this.schema.nodes.definition_description);
  }

  /*
   * Headings
   */
  @CachedAccessor()
  get changeBlockToH1(): Command {
    return setBlockType(this.schema.nodes.h1, { class: undefined });
  }

  @CachedAccessor()
  get changeBlockToH2(): Command {
    return setBlockType(this.schema.nodes.h2, { class: undefined });
  }

  @CachedAccessor()
  get changeBlockToH3(): Command {
    return setBlockType(this.schema.nodes.h3, { class: undefined });
  }

  @CachedAccessor()
  get changeBlockToH4(): Command {
    return setBlockType(this.schema.nodes.h4, { class: undefined });
  }

  @CachedAccessor()
  get changeBlockToH5(): Command {
    return setBlockType(this.schema.nodes.h5, { class: undefined });
  }

  @CachedAccessor()
  get changeBlockToH6(): Command {
    return setBlockType(this.schema.nodes.h6, { class: undefined });
  }

  /*
   * Multiple Choice
   */
  @CachedAccessor()
  get appendMultipleChoiceItem(): Command {
    return appendMultipleChoiceItem([this.schema.nodes.madcapmultiplechoiceitem, this.schema.nodes.madcapquestion], this.schema.nodes.madcapmultiplechoiceitem);
  }

  /*
   * Paragraph
   */
  @CachedAccessor()
  get changeBlockToParagraph(): Command {
    return setBlockType(this.schema.nodes.paragraph, { class: undefined });
  }

  /*
   * Images
   */
  // Used for inserting a project image
  @CachedAccessor()
  get insertOrEditImage(): InsertInlineNodeCommand {
    return insertInlineNode(this.schema.nodes.image, attrs => attrs);
  }

  // Used for inserting new images that must be added to the server first
  @CachedAccessor()
  get insertImagePlaceholders(): CreateImagePlaceholdersCommand {
    return insertImagePlaceholders();
  }

  /*
   * Variables
   */
  @CachedAccessor()
  get insertVariable(): InsertInlineNodeCommand {
    return insertInlineNode(this.schema.nodes.madcapvariable);
  }

  /*
   * Multimedia
   */
  @CachedAccessor()
  get insertOrEditMultimedia(): InsertInlineNodeCommand {
    return insertInlineNode(this.schema.nodes.madcapmultimedia, attrs => {
      if (attrs['MadCap:HTML5Video']) {
        return {
          ...attrs,
          'MadCap:HTML5Video': true,
          'MadCap:Param_controls': 'true', // This attribute is not simply a boolean value, it can be other string values as well so use a string value.
          'MadCap:Param_muted': true,
          'MadCap:Param_loop': true,
          'MadCap:Param_autoplay': true
        };
      } else {
        return attrs;
      }
    });
  }
  @CachedAccessor()
  get insertOrEdit3dModel(): InsertInlineNodeCommand {
    return insertInlineNode(this.schema.nodes.madcapmodel3d);
  }

  /*
  * Links
  */
  @CachedAccessor()
  get insertOrEditLink(): LinkNodeCommand {
    return insertOrEditLink(this.schema.nodes.link, this.schema.nodes.link);
  }

  @CachedAccessor()
  get insertOrEditCrossRefLink(): LinkNodeCommand {
    return insertOrEditLink(this.schema.nodes.madcapcrossreference, this.schema.nodes.link);
  }

  @CachedAccessor()
  get deleteLink(): Command {
    return liftFromInlineNode(this.schema.nodes.link, {
      liftAncestorForEmptySelection: true
    });
  }

  @CachedAccessor()
  get deleteCrossReference(): Command {
    return liftFromInlineNode(this.schema.nodes.madcapcrossreference, {
      liftAncestorForEmptySelection: true
    });
  }

  /*
  * Proxies
  */
  @CachedAccessor()
  get insertBodyProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcapbodyproxy);
  }

  @CachedAccessor()
  get insertBreadcrumbsProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcapbreadcrumbsproxy);
  }

  @CachedAccessor()
  get insertTopicToolbarProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcaptopictoolbarproxy);
  }

  @CachedAccessor()
  get insertMiniTOCProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcapminitocproxy);
  }

  @CachedAccessor()
  get insertGlossaryProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcapglossaryproxy);
  }

  @CachedAccessor()
  get insertSearchBarProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcapsearchbarproxy);
  }

  @CachedAccessor()
  get insertMenuProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcapmenuproxy);
  }

  @CachedAccessor()
  get insertCentralAccountProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcapcentralaccountproxy);
  }

  @CachedAccessor()
  get insertELearningToolbarProxy(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.madcapelearningtoolbarproxy);
  }

  /*
   * Equations
   */
  @CachedAccessor()
  get insertOrEditEquation(): Command {
    return insertOrEditEquation(this.schema.nodes.madcapequation);
  }

  /*
  * Snippets
  */
  @CachedAccessor()
  get insertSnippet(): CreateSnippetNodeCommand {
    return insertSnippet(this.schema.nodes.madcapsnippettext, this.schema.nodes.madcapsnippetblock);
  }

  /*
   * Tables
   */
  @CachedAccessor()
  get insertTable(): InsertBlockNodeCommand {
    return insertBlockNode(this.schema.nodes.table, {
      tbodyType: this.schema.nodes.tbody,
      rowType: this.schema.nodes.table_row,
      cellType: this.schema.nodes.table_cell,
      colGroupType: this.schema.nodes.colgroup,
      columnType: this.schema.nodes.col
    })
  }

  /*
   * Typography
   */
  @CachedAccessor()
  get wrapInBold(): Command {
    return wrapInInlineNode(this.schema.nodes.b, { wrapParentForEmptySelection: true });
  }

  @CachedAccessor()
  get unwrapBold(): Command {
    return liftFromInlineNode(this.schema.nodes.b);
  }

  @CachedAccessor()
  get toggleBold(): Command {
    return toggleInlineNode(this.schema.nodes.b, { wrapParentForEmptySelection: true });
  }

  @CachedAccessor()
  get wrapInItalics(): Command {
    return wrapInInlineNode(this.schema.nodes.i, { wrapParentForEmptySelection: true });
  }

  @CachedAccessor()
  get unwrapItalics(): Command {
    return liftFromInlineNode(this.schema.nodes.i);
  }

  @CachedAccessor()
  get toggleItalics(): Command {
    return toggleInlineNode(this.schema.nodes.i, { wrapParentForEmptySelection: true });
  }

  @CachedAccessor()
  get wrapInUnderline(): Command {
    return wrapInInlineNode(this.schema.nodes.u, { wrapParentForEmptySelection: true });
  }

  @CachedAccessor()
  get unwrapUnderline(): Command {
    return liftFromInlineNode(this.schema.nodes.u);
  }

  @CachedAccessor()
  get toggleUnderline(): Command {
    return toggleInlineNode(this.schema.nodes.u, { wrapParentForEmptySelection: true });
  }

  /*
   * Drop-downs
   */
  @CachedAccessor()
  get insertDropDown(): Command {
    return insertDropDown(this.schema.nodes.madcapdropdown, this.schema.nodes.madcapdropdownhead, this.schema.nodes.madcapdropdownhotspot, this.schema.nodes.madcapdropdownbody);
  }

  @CachedAccessor()
  get setDropDownHotspot(): Command {
    return setDropDownHotspot(this.schema.nodes.madcapdropdownhead, this.schema.nodes.madcapdropdownhotspot);
  }

  @CachedAccessor()
  get moveFromDropDown(): Command {
    return moveFromDropDown(this.schema.nodes.madcapdropdown, this.schema.nodes.madcapdropdownbody);
  }

  /*
   * Conditions
   */
  @CachedAccessor()
  get setConditions(): ApplyConditionsCommand {
    return applyConditions();
  }

  /*
   * Styles Panel
   */
  @CachedAccessor()
  get setShowStylesPanel(): FlareComputedStylesheetSetShowStylesCommand {
    return setShowStylesPanel();
  }

  @CachedAccessor()
  get toggleShowStylesPanel(): Command {
    return toggleShowStylesPanel();
  }

  @CachedAccessor()
  get setFormat(): SetSelectionFormatCommand {
    return setSelectionFormat();
  }

  /*
   * Styles
   */
  @CachedAccessor()
  get setShowStyles(): FlareComputedStylesheetSetShowStylesCommand {
    return setShowStyles();
  }

  @CachedAccessor()
  get toggleShowStyles(): Command {
    return toggleShowStyles();
  }

  /*
   * Tracked Changes
   */
  @CachedAccessor()
  get setTrackChanges(): TrackedChangesSetTrackChangesCommand {
    return setTrackChanges();
  }

  @CachedAccessor()
  get toggleTrackChanges(): Command {
    return toggleTrackChanges();
  }
}
